import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { Switch } from "../../components/ui/switch";
import { Field, ImageField, inputCls } from "./fields";

const EMPTY = { name: "", brand: "", size: "", expiry: "", price: 0, badge: "", image: "", in_stock: true, order: 0 };

const ProductsTab = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/products");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((p) => `${p.name} ${p.brand}`.toLowerCase().includes(q.toLowerCase())), [items, q]);

  const save = async () => {
    if (!edit.name.trim() || !edit.brand.trim()) return toast.error("Name and brand are required");
    setSaving(true);
    try {
      const body = { ...EMPTY, ...edit, price: Number(edit.price) || 0, order: Number(edit.order) || 0 };
      delete body.id; delete body.created_at;
      if (edit.id) await api.put(`/products/${edit.id}`, body);
      else await api.post("/products", body);
      toast.success(edit.id ? "Product updated" : "Product added");
      setEdit(null);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleStock = async (p) => {
    const body = { ...p, in_stock: !p.in_stock };
    delete body.id; delete body.created_at;
    await api.put(`/products/${p.id}`, body);
    await load();
    onChange?.();
  };

  const remove = async () => {
    await api.delete(`/products/${del.id}`);
    toast.success("Product deleted");
    setDel(null);
    await load();
    onChange?.();
  };

  const set = (k) => (e) => setEdit({ ...edit, [k]: e?.target ? e.target.value : e });

  return (
    <div data-testid="products-tab">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a9a97]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className={`${inputCls} pl-10`} data-testid="admin-product-search" />
        </div>
        <button onClick={() => setEdit({ ...EMPTY, order: items.length })} className="sw-btn-primary !py-2.5" data-testid="add-product-btn"><Plus size={17} /> Add product</button>
      </div>

      {loading ? <Loader2 className="animate-spin text-[#0e8c7f]" /> : (
        <div className="overflow-hidden rounded-2xl border border-[#e3f0ed] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-[14.5px]">
              <thead className="bg-[#f4fbf9] text-[12.5px] uppercase tracking-wider text-[#5b6d6a]">
                <tr><th className="px-4 py-3">Product</th><th className="px-4 py-3">Brand</th><th className="px-4 py-3">Size / Expiry</th><th className="px-4 py-3">Payout</th><th className="px-4 py-3">Badge</th><th className="px-4 py-3">In stock</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-[#eef5f3]" data-testid="admin-product-row">
                    <td className="px-4 py-3"><div className="flex items-center gap-3">{p.image ? <img src={imgUrl(p.image)} alt="" className="h-11 w-11 rounded-lg border border-[#eef5f3] bg-white object-contain" /> : <span className="h-11 w-11 rounded-lg border border-[#eef5f3] bg-[#f4f8f7]" />}<span className="font-semibold text-[#0f2e2a]">{p.name}</span></div></td>
                    <td className="px-4 py-3">{p.brand}</td>
                    <td className="px-4 py-3 text-[#5b6d6a]">{p.size}<br /><span className="text-[12.5px]">{p.expiry}</span></td>
                    <td className="px-4 py-3 font-bold text-[#0e8c7f]">${p.price}</td>
                    <td className="px-4 py-3">{p.badge && <span className="rounded-full bg-[#fff3dc] px-2.5 py-1 text-[12px] font-semibold text-[#a0620a]">{p.badge}</span>}</td>
                    <td className="px-4 py-3"><Switch checked={p.in_stock} onCheckedChange={() => toggleStock(p)} /></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEdit(p)} className="rounded-lg p-2 text-[#0e8c7f] hover:bg-[#e8f8f4]" aria-label="Edit" data-testid="edit-product-btn"><Pencil size={16} /></button>
                      <button onClick={() => setDel(p)} className="rounded-lg p-2 text-red-500 hover:bg-red-50" aria-label="Delete" data-testid="delete-product-btn"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-[620px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit product" : "Add product"}</DialogTitle><DialogDescription>Product details shown on the website and price list.</DialogDescription></DialogHeader>
          {edit && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="product-form">
              <div className="sm:col-span-2"><Field label="Product name *"><input className={inputCls} value={edit.name} onChange={set("name")} data-testid="product-name-input" /></Field></div>
              <Field label="Brand *"><input className={inputCls} list="brand-list" value={edit.brand} onChange={set("brand")} data-testid="product-brand-input" /></Field>
              <datalist id="brand-list">{[...new Set(items.map((i) => i.brand))].map((b) => <option key={b} value={b} />)}</datalist>
              <Field label="Payout up to ($)"><input type="number" className={inputCls} value={edit.price} onChange={set("price")} data-testid="product-price-input" /></Field>
              <Field label="Size / count"><input className={inputCls} value={edit.size} onChange={set("size")} placeholder="e.g. 3 ct" data-testid="product-size-input" /></Field>
              <Field label="Expiry note"><input className={inputCls} value={edit.expiry} onChange={set("expiry")} placeholder="e.g. Expires 6+ months" /></Field>
              <Field label="Badge (optional)"><input className={inputCls} value={edit.badge} onChange={set("badge")} placeholder="e.g. Most Wanted" /></Field>
              <Field label="Display order"><input type="number" className={inputCls} value={edit.order} onChange={set("order")} /></Field>
              <div className="sm:col-span-2"><Field label="Image"><ImageField value={edit.image} onChange={set("image")} testid="product-image-input" /></Field></div>
              <label className="flex items-center gap-3 sm:col-span-2"><Switch checked={edit.in_stock} onCheckedChange={set("in_stock")} /><span className="text-[14px] font-medium">Currently buying (in stock)</span></label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="sw-btn-outline !py-2">Cancel</button>
            <button onClick={save} disabled={saving} className="sw-btn-primary !py-2.5 disabled:opacity-60" data-testid="save-product-btn">{saving && <Loader2 size={16} className="animate-spin" />}Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete product?</AlertDialogTitle>
            <AlertDialogDescription>“{del?.name}” will be removed from your website.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsTab;
